import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSignalR } from "src/hooks";
import { TaskItemDetailDto } from "src/http/types/taskItems/TaskItemDetailDto";
import { configuration } from "../../../configuration";
import { FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  taskItemGuid: string;
};

const useDeleteTaskItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteTaskItemRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteTaskItemRequest };
};

const useDeleteTaskItem = () => {
  const { deleteTaskItemRequest } = useDeleteTaskItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteTaskItemRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined = undefined;

      queryClient.cancelQueries(queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid));
      cachedDetailData = queryClient.getQueryData<TaskItemDetailDto>(
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid)
      );
      queryClient.removeQueries(queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid));

      return { cachedDetailData };
    },
    onSuccess: (_data, _variables, _context) => {
      signalR.sendRefetchTeamTaskItems();
      signalR.sendRefetchUserTaskItems();
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, _variables) => {
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);
    },
  });
};

export { useDeleteTaskItem };
